import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { editorKind, initialEditorText, parseEditorText } from './fieldEditing';

// Inline editor for one structured-data field (doc-32 phase b). Replaces the
// value in its <dd> while open: typed input per the field's editor kind,
// Cmd+Enter saves, Esc cancels. Parse/validation errors render inline and
// keep the editor open; saving marks the field human/locked server-side.
export default function StructuredFieldEditor({ field, value, onSave, onCancel }) {
  const kind = useMemo(() => editorKind(field, value), [field, value]);
  const [text, setText] = useState(() => initialEditorText(field, value));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    let parsed;
    try {
      parsed = parseEditorText(field, kind, text);
    } catch (err) {
      setError(err.message);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(parsed); // parent closes the editor on success
    } catch (err) {
      setError(err?.message || 'Save failed');
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    }
  };

  const inputProps = {
    value: text,
    onChange: (e) => setText(e.target.value),
    onKeyDown: handleKeyDown,
    autoFocus: true,
    disabled: saving,
    'aria-label': `Edit ${field.label || field.key}`,
  };

  let input;
  if (kind === 'select') {
    const options = field.type === 'boolean'
      ? [['true', 'Yes'], ['false', 'No']]
      : (field.options || []).map((o) => [String(o), String(o)]);
    input = (
      <select className="select" {...inputProps}>
        <option value="">—</option>
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
    );
  } else if (kind === 'number') {
    input = <input className="input" type="number" step="any" {...inputProps} />;
  } else if (kind === 'date') {
    input = <input className="input" type="date" {...inputProps} />;
  } else if (kind === 'json') {
    input = (
      <textarea
        className="textarea mono"
        rows={5}
        style={{ fontSize: 12, resize: 'vertical' }}
        {...inputProps}
      />
    );
  } else {
    input = (
      <textarea
        className="textarea"
        rows={kind === 'lines' ? 3 : 2}
        style={{ resize: 'vertical' }}
        {...inputProps}
      />
    );
  }

  return (
    <div className="stack-sm" style={{ maxWidth: 480 }}>
      {input}
      {kind === 'lines' && (
        <div className="muted" style={{ fontSize: 11.5 }}>One item per line</div>
      )}
      {error && (
        <div style={{ color: 'var(--text-critical)', fontSize: 12 }}>{error}</div>
      )}
      <div className="row" style={{ gap: 6 }}>
        <button type="button" className="btn ghost xs" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn primary xs" onClick={save} disabled={saving}>
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          Save
        </button>
      </div>
    </div>
  );
}
